import { ReactElement } from 'react'
import { Rectangle, RectangleProps } from 'recharts'

import { SalesReportGraphValues } from '../../../../types/salesReportGraphValues'
import { GraphBarType } from '../../enums/graphBarType'
import { BarLayout } from '../../types/barLayout'
import { barColors } from './constants/barColors'
import { getBarRadius } from './utils/getBarRadius'

const BAR_GAP = 2

interface SalesReportGraphBarProps extends Omit<RectangleProps, 'values'> {
  barLayout: BarLayout
  dataKey: GraphBarType
  isHovered?: boolean
  values: SalesReportGraphValues
}

export const SalesReportGraphBar = ({
  barLayout,
  dataKey,
  isHovered,
  height,
  values,
  width,
  x,
  y,
  ...rest
}: SalesReportGraphBarProps): ReactElement | null => {
  if (!height || !values || !width || !x || !y) {
    return null
  }

  const { unpaid, paid } = values
  const radius = getBarRadius({ paid, unpaid, barType: dataKey, barLayout })

  const isVertical = barLayout === 'vertical'
  const hasSiblingBar = dataKey === GraphBarType.Unpaid && paid > 0

  // Deltas for spacing adjustments
  const verticalDelta = hasSiblingBar && isVertical ? BAR_GAP / 2 : 0
  const horizontalDelta = hasSiblingBar && !isVertical ? BAR_GAP : 0
  // Adjusted positions and values
  const adjustedFill = barColors[isHovered ? 'hovered' : 'default'][dataKey]
  const adjustedHeight = height - verticalDelta
  const adjustedY = y - verticalDelta
  const adjustedWidth = width - horizontalDelta
  const adjustedX = x + horizontalDelta
  // Ensure non-negative values
  const sanitizedHeight = Math.max(0, adjustedHeight)
  const sanitizedWidth = Math.max(0, adjustedWidth)

  const opacity = dataKey === GraphBarType.Paid && paid === 0 ? 0 : 1

  return (
    <Rectangle
      {...rest}
      fill={adjustedFill}
      radius={radius}
      height={sanitizedHeight}
      width={sanitizedWidth}
      x={adjustedX}
      y={adjustedY}
      opacity={opacity}
    />
  )
}
